import {
  Button,
  HStack,
  Input,
  RadioGroup,
  Radio,
  VStack,
  IconButton,
  Show,
} from "@chakra-ui/react";
import { FractionChildProps, FractionProps, PostFraction } from "../Fraction";
import { ExternalLink, FileDownIcon, Plus, Trash2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import {
  createDownloadUrlForGoogleDrive,
  isGoogleDriveUrl,
} from "../../../../../Helpers/FileDownload";
import { DisplayMode } from "../../Post";

enum ButtonType {
  DOWNLOAD_FILE = "download_file",
  GO_TO_LINK = "go_to_link",
}

export interface LinkState {
  type: ButtonType;
  text: string;
  url: string;
}

export type LinkFractionState = LinkState[] | undefined;

function LinkEditorAndViewer(props: FractionChildProps<LinkFractionState>) {
  if (props.mode === DisplayMode.EDIT) {
    return <LinkEditor {...props} />;
  } else {
    return <LinkViewer {...props} />;
  }
}

function LinkViewer(props: FractionChildProps<LinkFractionState>) {
  if (props.state === undefined || props.state.length === 0) {
    return <div>[[[Lägg till länkar och filer här]]]</div>;
  }

  const handleDownload = (url: string, text: string) => {
    return () => {
      const fileUrl = isGoogleDriveUrl(url)
        ? createDownloadUrlForGoogleDrive(url)
        : url;
      const link = document.createElement("a");
      link.download = text.replace(" ", "_");
      link.href = fileUrl;
      link.click();
    };
  };

  return (
    <>
      <Show above="sm">
        <HStack marginLeft={"15px"}>
          {props.state!.map((s, index) => {
            if (s.type === ButtonType.DOWNLOAD_FILE) {
              return (
                <Button
                  key={`download_${index}`}
                  background={"blue.400"}
                  colorScheme="blue"
                  leftIcon={<FileDownIcon />}
                  onClick={handleDownload(s.url, s.text)}
                >
                  {s.text}
                </Button>
              );
            } else {
              return (
                <Button
                  key={`link_${index}`}
                  background={"blue.400"}
                  colorScheme="blue"
                  leftIcon={<ExternalLink />}
                  onClick={() => {
                    window.open(s.url, "_blank", "noopener,noreferrer");
                  }}
                >
                  {s.text}
                </Button>
              );
            }
          })}
        </HStack>
      </Show>
      <Show below="sm">
        <VStack marginLeft={"15px"}>
          {props.state!.map((s, index) => {
            if (s.type === ButtonType.DOWNLOAD_FILE) {
              return (
                <Button
                  key={`download_${index}`}
                  background={"blue.400"}
                  colorScheme="blue"
                  leftIcon={<FileDownIcon />}
                  onClick={handleDownload(s.url, s.text)}
                >
                  {s.text}
                </Button>
              );
            } else {
              return (
                <Button
                  key={`link_${index}`}
                  background={"blue.400"}
                  colorScheme="blue"
                  leftIcon={<ExternalLink />}
                  onClick={() => {
                    window.open(s.url, "_blank", "noopener,noreferrer");
                  }}
                >
                  {s.text}
                </Button>
              );
            }
          })}
        </VStack>
      </Show>
    </>
  );
}

function LinkEditor(props: FractionChildProps<LinkFractionState>) {
  const [state, setState] = useState<LinkState[]>(
    props.state !== undefined && props.state.length > 0
      ? props.state
      : [{ text: "", type: ButtonType.GO_TO_LINK, url: "" }]
  );

  useEffect(() => {
    props.onChange!(
      state
        .filter((s) => s.text !== "" && s.url !== "")
        .map((s) => ({ ...s, text: s.text.trim(), url: s.url.trim() }))
    );
  }, [state]);

  return (
    <div
      className="base_editor"
      style={{
        margin: "20px",
        marginTop: "20px",
        borderColor: "#999999",
        borderRadius: "10px",
        borderWidth: "1px",
        background: "white",
      }}
    >
      <div
        style={{
          margin: "20px",
          marginTop: "20px",
        }}
      >
        <VStack align={"left"}>
          {state.map((s, i) => (
            <HStack key={`radio_group_${i}`}>
              <RadioGroup
                key={`radio_${i}`}
                colorScheme="green"
                onChange={(val) => {
                  setState((old) => {
                    return [
                      ...old.slice(0, i),
                      { ...old[i], type: val as ButtonType },
                      ...old.slice(i + 1),
                    ];
                  });
                }}
                value={s.type}
                defaultValue={ButtonType.GO_TO_LINK}
              >
                <HStack>
                  <Radio value={ButtonType.DOWNLOAD_FILE}>Nedladdning</Radio>
                  <Radio value={ButtonType.GO_TO_LINK}>Länk</Radio>
                </HStack>
              </RadioGroup>
              <Input
                width={"sm"}
                placeholder="Text (Ex: Till anmälan)"
                value={s.text}
                onChange={(e) => {
                  setState((old) => {
                    return [
                      ...old.slice(0, i),
                      { ...old[i], text: e.target.value },
                      ...old.slice(i + 1),
                    ];
                  });
                }}
              />
              <Input
                placeholder="URL (Ex: https://www.example.com)"
                textColor={"black"}
                value={s.url}
                onChange={(e) => {
                  setState((old) => {
                    return [
                      ...old.slice(0, i),
                      { ...old[i], url: e.target.value },
                      ...old.slice(i + 1),
                    ];
                  });
                }}
              />
              <IconButton
                colorScheme="green"
                variant="ghost"
                aria-label="Lägg till"
                icon={<Plus />}
                onClick={() => {
                  setState((old) => {
                    return [
                      ...old.slice(0, i + 1),
                      { ...old[i], url: "" },
                      ...old.slice(i + 1),
                    ];
                  });
                }}
              />
              <IconButton
                colorScheme="red"
                variant="ghost"
                aria-label="Lägg till"
                icon={<Trash2Icon />}
                onClick={() => {
                  setState((old) => {
                    return [...old.slice(0, i), ...old.slice(i + 1)];
                  });
                }}
              />
            </HStack>
          ))}
        </VStack>
      </div>
    </div>
  );
}

export const LinkFraction = (props: FractionProps<LinkFractionState>) => {
  return (
    <PostFraction {...props}>
      <LinkEditorAndViewer />
    </PostFraction>
  );
};
